import { useTranslation } from 'react-i18next'
import { z } from 'zod'

export interface VehicleItem {
  id?: string
  year: string
  make: string
  model: string
  inop: boolean
  type: string
  lot: string
  vin: string
}

export interface VehiclesFormData {
  vehicles: VehicleItem[]
}

// Canonical catalog mirrored from the shipment service (`VehicleType` enum /
// `GET /vehicles/types`). Deliberately static — it feeds the Type dropdown
// and the set `normalizeVehicleType` validates stored values against.
// Keep in sync with the backend enum.
export const VEHICLE_TYPE_VALUES = [
  'sedan',
  'suv',
  'pickup',
  'van',
  'truck',
  'motorcycle',
  'boat',
  'rv',
  'atv',
  'trailer',
  'heavy_equipment',
  'other',
] as const

export type VehicleType = (typeof VEHICLE_TYPE_VALUES)[number]

const VEHICLE_TYPE_SET = new Set<string>(VEHICLE_TYPE_VALUES)

export function normalizeVehicleType(
  raw: string | null | undefined,
): VehicleType | '' {
  if (!raw) return ''
  const lower = raw.toLowerCase().trim()
  if (VEHICLE_TYPE_SET.has(lower)) return lower as VehicleType
  return ''
}

export const EMPTY_VEHICLE: VehicleItem = {
  year: '',
  make: '',
  model: '',
  inop: false,
  type: '',
  lot: '',
  vin: '',
}

export const EMPTY_VEHICLES_DEFAULTS: VehiclesFormData = {
  vehicles: [{ ...EMPTY_VEHICLE }],
}

const YEAR_PATTERN = /^(19|20)\d{2}$/
const VIN_LENGTH = 17

export function useVehiclesSchema() {
  const { t } = useTranslation()
  const required = t('forms.validation.required')
  const invalidYear = t('orderCreate.vehicles.invalidYear')
  const invalidVin = t('orderCreate.vehicles.invalidVin')
  const modelRequired = t('orderCreate.vehicles.modelRequired')

  const vehicleItem = z.object({
    id: z.string().optional(),
    year: z.string().min(1, required).regex(YEAR_PATTERN, invalidYear),
    make: z.string().min(1, required),
    model: z.string().min(1, modelRequired),
    inop: z.boolean().optional().default(false),
    type: z.string().optional().default(''),
    lot: z.string().optional().default(''),
    vin: z
      .string()
      .optional()
      .default('')
      .refine((v) => !v || v.length === VIN_LENGTH, invalidVin),
  })

  return z.object({
    vehicles: z.array(vehicleItem).min(1),
  })
}
